import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Heading,
  Grid,
  GridItem,
  Flex,
  VStack,
  Text,
  Icon,
  IconButton,
  Button,
  HStack,
  SimpleGrid,
  Image,
  Skeleton,
} from "@chakra-ui/react";
import { FaLeaf, FaBug, FaBell, FaCamera } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import useAxios from "../../utils/useAxios";
import placeholderImg from "./static/field_placeholder.png";
import localforage from "localforage";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { useMoistureColor } from "../../utils/colorUtils/soilMoistitureColorUtil";

export interface FieldTileProps {
  id: number;
  name: string;
  alertCount: number;
  diseaseType?: string;
  userDeviceId?: string;
  color?: string;
  imageUrl?: string;
  onImageChange: (fieldId: number, imageUrl: string) => void;
  soilStatus?: string;
  soilColor?: string;
  alertColor: string;
  soilLoading: boolean;
  alertsLoading: boolean;
}

const imageStore = localforage.createInstance({ name: "fieldImages" });

const FieldTile: React.FC<FieldTileProps> = ({
  id,
  name,
  alertCount,
  diseaseType,
  userDeviceId,
  color,
  imageUrl,
  onImageChange,
  soilStatus,
  soilColor,
  alertColor,
  soilLoading,
  alertsLoading,
}) => {
  const navigate = useNavigate();
  const api = useAxios();
  const { riskColor } = useMoistureColor();
  const [imageId, setImageId] = useState<number | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [cachedUrl, setCachedUrl] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    async function loadImage() {
      try {
        const { data } = await api.get("agtech/field-images/", {
          params: { field: id },
        });
        if (data.length > 0) {
          setImageId(data[0].id);
          // Notify parent only if no existing imageUrl prop, to avoid loops
          if (!imageUrl && data[0].image) {
            onImageChange(id, data[0].image);
          }
        }
      } catch (err) {
        console.error(err);
      }
    }
    loadImage();
    // only run on id change; onImageChange and imageUrl are intentionally omitted to prevent loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Cache image in IndexedDB via localforage, use cache then refresh in background
  useEffect(() => {
    let cancelled = false;
    const key = String(id);
    // load from cache
    imageStore
      .getItem<Blob>(key)
      .then((blob) => {
        if (blob && !cancelled) {
          setCachedUrl(URL.createObjectURL(blob));
        }
      })
      .catch(console.error);
    // fetch latest image through the authenticated API
    if (imageId) {
      api
        .get(`agtech/field-images/${imageId}/file/`, {
          responseType: "blob",
        })
        .then((res) => res.data as Blob)
        .then((blob) => {
          if (!cancelled) {
            imageStore.setItem(key, blob).catch(console.error);
            setCachedUrl(URL.createObjectURL(blob));
          }
        })
        .catch(console.error);
    }
    return () => {
      cancelled = true;
    };
    // api is a configured Axios instance recreated by the auth hook.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, imageId, imageUrl]);

  const handleView = () => navigate(`/fields/view/${id}`);
  const handleDiseaseView = () => navigate("/disease-prediction");

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const url = URL.createObjectURL(file);
    setPreview(url);

    const form = new FormData();
    form.append("field", String(id));
    form.append("image", file);

    try {
      let res;
      if (imageId) {
        res = await api.patch(`agtech/field-images/${imageId}/`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        res = await api.post("agtech/field-images/", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      const newUrl = res.data.image as string;
      onImageChange(id, newUrl);
      setImageId(res.data.id);
      setPreview(null);
    } catch (err) {
      console.error(err);
    }
  };

  const displayedUrl = preview
    ? preview
    : cachedUrl
      ? cachedUrl
      : imageUrl
        ? imageUrl.startsWith("http")
          ? imageUrl
          : `${window.location.origin}${imageUrl}`
        : null;

  const moistureStatus =
    alertCount === 0
      ? { label: <Trans>Saturated</Trans>, icon: FaLeaf }
      : { label: <Trans>Deficit</Trans>, icon: FaLeaf };

  const diseaseStatus =
    alertCount === 0
      ? { label: <Trans>Low Risk</Trans>, icon: FaBug }
      : { label: <Trans>High Risk</Trans>, icon: FaBug };

  const alertText = (
    <>
      {alertCount} {alertCount === 1 ? <Trans>Alert</Trans> : <Trans>Alerts</Trans>}
    </>
  );

  return (
    <Box key={id} p={{ base: 0, md: 4 }} borderRadius="md" boxShadow="sm">
      <Heading fontSize={28} mb={2}>
        {name}
      </Heading>

      <Box position="relative" borderRadius="md" overflow="hidden">
        {displayedUrl ? (
          <Image
            src={displayedUrl}
            alt={name}
            w="100%"
            h={{ base: "300px", md: "450px", lg: "550px" }}
            objectFit="cover"
          />
        ) : (
          <Image
            src={placeholderImg}
            alt="Field placeholder"
            w="100%"
            h={{ base: "300px", md: "450px", lg: "550px" }}
            bg="#eee"
            objectFit="cover"
          />
        )}

        <IconButton
          aria-label="Upload image"
          icon={<FaCamera />}
          position="absolute"
          top={0}
          right={0}
          size={{ base: "sm", md: "md" }}
          h="60px"
          w="60px"
          variant="solid"
          bg="green.900"
          color="white"
          _hover={{ bg: "green.800" }}
          borderRadius={0}
          borderBottomLeftRadius="md"
          zIndex={2}
          onClick={() => fileInputRef.current?.click()}
        />

        <Button
          position="absolute"
          bottom={0}
          left={0}
          w={{ base: "170px", md: "200px" }}
          h={{ base: "45px", md: "55px" }}
          variant="solid"
          bg="green.900"
          color="white"
          _hover={{ bg: "green.800" }}
          borderRadius={0}
          borderBottomLeftRadius="md"
          borderTopRightRadius="md"
          zIndex={2}
          onClick={handleView}
        >
          <Trans>View Field</Trans>
        </Button>

        <input
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          ref={fileInputRef}
          onChange={handleFileChange}
          placeholder={t`Upload Field Image`}
        />
      </Box>
      <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={4} mt={4}>
        <Skeleton isLoaded={!soilLoading} borderRadius="md">
          <GridItem
            bg={soilColor || "gray.200"}
            p={4}
            borderRadius="md"
            cursor="pointer"
            onClick={handleView}
            minW={0}
          >
            <Flex align="center" minW={0}>
              <Icon as={moistureStatus.icon} boxSize={5} mr={2} flexShrink={0} />
              <VStack spacing={0} align="start" minW={0}>
                <Text fontWeight="bold" noOfLines={1}>
                  <Trans>{soilStatus || "Soil Info"}</Trans>
                </Text>
                <Text fontSize="sm" noOfLines={1}>
                  <Trans>Soil Moisture</Trans>
                </Text>
              </VStack>
            </Flex>
          </GridItem>
        </Skeleton>

        <GridItem
          bg={riskColor}
          p={4}
          borderRadius="md"
          cursor="pointer"
          onClick={handleDiseaseView}
          minW={0}
        >
          <Flex align="center" minW={0}>
            <Icon as={diseaseStatus.icon} boxSize={5} mr={2} flexShrink={0} />
            <VStack spacing={0} align="start" minW={0}>
              <Text fontWeight="bold" noOfLines={1}>
                <Trans>Risk Info</Trans>
              </Text>
              <Text fontSize="sm" noOfLines={1}>
                <Trans>Prototype Coming Soon</Trans>
              </Text>
            </VStack>
          </Flex>
        </GridItem>

        <Skeleton isLoaded={!alertsLoading} borderRadius="md">
          <GridItem
            bg={alertColor}
            p={4}
            borderRadius="md"
            minW={0}
            onClick={() => navigate(`/fields/alarms/${id}`)}
            cursor={"pointer"}
            h="100%"
          >
            <Flex align="center" minW={0}>
              <Icon as={FaBell} boxSize={5} mr={2} flexShrink={0} />
              <VStack spacing={0} align="start" minW={0}>
                <Text fontWeight="bold" noOfLines={1}>
                  <Trans>{alertText}</Trans>
                </Text>
                <Text fontSize="sm" noOfLines={1}>
                  <Trans>Active Alerts</Trans>
                </Text>
              </VStack>
            </Flex>
          </GridItem>
        </Skeleton>
      </SimpleGrid>
    </Box>
  );
};

export default FieldTile;
